/**
 * «Перенести в другой проект» (Spec 018). The task keeps its ID, people,
 * discussion and history; subtasks go along and cannot move alone. A refusal
 * (e.g. the project has no status for a cancelled task) comes back in words.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderInput } from "lucide-react";

import { Projects, Tasks } from "@/api/resources";
import type { TaskOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useToast } from "@/components/ui/Toast";

export function TaskMove({ task }: { task: TaskOut }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<{ id: string; title: string }>();
  const [error, setError] = useState<string>();
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: () => Projects.list(),
    enabled: open,
  });
  const move = useMutation({
    mutationFn: (projectId: string) => Tasks.move(task.id, projectId, task.version),
    onSuccess: async () => {
      setOpen(false);
      setChosen(undefined);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["task", task.id] }),
        qc.invalidateQueries({ queryKey: ["tasks"] }),
        qc.invalidateQueries({ queryKey: ["project-tasks"] }),
        // Task counts and progress of both projects change.
        qc.invalidateQueries({ queryKey: ["projects"] }),
        qc.invalidateQueries({ queryKey: ["project"] }),
      ]);
      toast.success("Задача перенесена");
    },
    onError: (e: Error) => setError(e.message),
  });

  if (task.parent_task_id) return null; // a subtask moves with its parent

  if (!open) {
    return (
      <Button
        size="compact"
        variant="ghost"
        onClick={() => {
          setError(undefined);
          setChosen(undefined);
          setOpen(true);
        }}
      >
        <FolderInput className="h-4 w-4" aria-hidden="true" />
        Перенести в другой проект
      </Button>
    );
  }
  const targets = (projects.data ?? []).filter((p) => p.id !== task.project_id && !p.archived_at);
  return (
    <Card className="space-y-2">
      <p className="text-meta text-hint">
        ID, исполнитель, обсуждение и история останутся с задачей, подзадачи переедут вместе с ней.
      </p>
      {chosen ? (
        <>
          {/* A second tap: a move back does not always restore the same status. */}
          <p className="text-body">
            Перенести «{task.title}» в «{chosen.title}»?
          </p>
          <div className="flex gap-2">
            <Button size="compact" loading={move.isPending} onClick={() => move.mutate(chosen.id)}>
              Перенести
            </Button>
            <Button size="compact" variant="ghost" onClick={() => setChosen(undefined)}>
              Другой проект
            </Button>
          </div>
        </>
      ) : (
        targets.map((p) => (
          <Button
            key={p.id}
            variant="ghost"
            className="h-auto min-h-touch w-full justify-start whitespace-normal py-2 text-left"
            onClick={() => setChosen({ id: p.id, title: p.title })}
          >
            {p.title}
          </Button>
        ))
      )}
      {projects.isSuccess && targets.length === 0 && (
        <p className="text-meta text-hint">Других проектов нет.</p>
      )}
      {error && (
        <p role="alert" className="text-meta text-dstr">
          {error}
        </p>
      )}
      <Button size="compact" variant="ghost" onClick={() => setOpen(false)}>
        Отмена
      </Button>
    </Card>
  );
}
